import {
    ApprovalScopeEnum,
    Expense,
    ExpenseKindEnum,
    ExpensePaymentFlowInformationEnum,
    ExpenseStatusEnum,
    Hospitality,
    InboxInvoice,
    ItemSplit,
    Mileage,
    PerDiem,
    PriceSourceEnum,
    RightEnum,
    SplitTypeEnum,
    Tax,
    TripFolder,
} from "@finway-group/shared/lib/models"
import { type PrevAndNextDocsResponse } from "@finway-group/shared/lib/models/adjacent/prevAndNextDocsResponse.interface"
import { InvoiceSplit, LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { Employee, GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { roundNumberTo2Decimals, toAmount, toDinero } from "@finway-group/shared/lib/utils"
import { calculatePercentage } from "@finway-group/shared/lib/utils/expense.utils"
import { PriceCalculator } from "@finway-group/shared/lib/utils/price.calculator.utils"
import { Modal } from "antd"
import { FormInstance, FormListOperation } from "antd/lib/form"
import { MenuInfo } from "rc-menu/lib/interface"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { RouterChildContext } from "react-router-dom"
import { useDebouncedCallback } from "use-debounce/lib"
import { ExpenseData, Filler } from "~Components/modals/expenseCreateForm.context"
import { expenseInitializer } from "~Features/pages/expenses/expenseInitializer"
import { DEBOUNCE_DURATION_LONG, DEBOUNCE_DURATION_SHORT, NO_TAX_RATE } from "~Shared/config/consts"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { useTaxes } from "~Shared/hooks/tax.hooks"
import { i18n } from "~Shared/locales/i18n"
import { defaultQueryParams } from "~Shared/queries/vendors/consts"
import { useVendorList } from "~Shared/queries/vendors/vendor.queries"
import { CompanyScopedRoutePathEnum } from "~Shared/router/RoutePath.enum"
import { ApprovalProcessService, AuthzService, ExpenseHttpService, ExpenseService, NotificationService, TaxService } from "~Shared/services"
import { DialogService } from "~Shared/services/dialog.service"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { store } from "~Shared/store"
import {
    approveExpense,
    approveFolder,
    deleteExpense,
    detachOneExpense,
    rejectExpense,
    reviewExpense,
    reviewMultipleExpenses,
    sendReminder,
    setAllExpensesAsDone,
    setExpenseDone,
    setExpensePaid,
    setMultipleExpensesAsDoneByExpenseIds,
    setMultipleExpensesAsPaid,
    submitFolder,
    updateExpense,
} from "~Shared/store/actions/expense/expenseActions"
import { ExpenseCountInterface } from "~Shared/store/actions/expense/expenseTypes"
import { RootState } from "~Shared/store/rootState.interface"
import {
    checkIsApprovalProcessNotStarted,
    checkIsPurchaseRequest,
    createCancelExpenseRequestTranslationStrings,
    createLinkToRequestFunction,
    getExpenseModelByKind,
    isFolderExpense,
    syncTotalExpenseAmountsToItems,
} from "~Shared/utils/expense.utils"
import { getFolderProgressionInfo, hasMultipleCurrencies } from "~Shared/utils/folder.utils"
import { addPrices, copyDeeply, doAllElementsHaveTheSameFieldValue, getTax, isExtendedAlphanumeric, isRightGranted, subtractPrices } from "~Shared/utils/helper.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { generatePath } from "~Shared/utils/routing.utils"
import { useCompany } from "./company.hooks"
import { useCostCenters } from "./costCenter.hooks"
import { useEmployees } from "./employee.hooks"
import { useExpenseAccounts } from "./expenseAccount.hooks"
import { useRolesMap } from "./role.hooks"
import { useActiveCompanyProfile } from "./user.hooks"

export const useExpenseCounts = (): ExpenseCountInterface => useSelector(({ expenses }: RootState) => expenses.counts)

export const useInboxInvoicesCount = () => useSelector(({ inboxInvoices }: RootState) => inboxInvoices.count)

export const useShouldRefetchExpenses = () => useSelector(({ expenses }: RootState) => expenses.shouldRefetch)

export const useShouldRefetchInbox = () => useSelector(({ inboxInvoices }: RootState) => inboxInvoices.shouldRefetch)

export const useExpenses = (excludeDeleted: boolean = false) => {
    const { items, totalPages, totalDocs, counts } = useSelector(({ expenses }: RootState) => expenses)
    let expenses = items.map((expense: Expense) => new Expense(expense))

    if (excludeDeleted) {
        expenses = expenses?.filter((expense: Expense) => !expense.deleted)
    }

    return {
        expenses,
        totalPages,
        totalDocs,
        counts,
    }
}

export const useInboxInvoices = () => {
    const { items, totalPages, totalDocs, count } = useSelector(({ inboxInvoices }: RootState) => inboxInvoices)
    const inboxInvoices = items.map((i: InboxInvoice) => new InboxInvoice(i))

    return {
        inboxInvoices,
        totalPages,
        totalDocs,
        count,
    }
}

export const useSubscriptions = (excludeDeleted: boolean = false) => {
    const { expenses } = useExpenses(excludeDeleted)
    return expenses.filter((expense: Expense) => expense.isSubscription() && expense.isInitialSubscription())
}

export const useExpense = () => {
    const expenseState = useSelector(({ expenses }: RootState) => expenses.item)
    const ExpenseModel = getExpenseModelByKind(expenseState.kind)

    const expense = new ExpenseModel(expenseState)

    return expense
}

export const useActiveTab = () => useSelector(({ expenses }: RootState) => expenses.activeTab)

export const useExpenseSwitching = (expenseId: string | undefined, expenseStatus: ExpenseStatusEnum | undefined, requestedBy: string | undefined, expense: Expense) => {
    const [adjacentExpenses, setAdjacentExpenses] = useState<{ prevExpense: string; nextExpense: string }>({ prevExpense: "", nextExpense: "" })
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [adjacentExpenseLabel, setAdjacentExpenseLabel] = useState("")
    const activeTab = useActiveTab()
    const { prevExpense, nextExpense } = adjacentExpenses
    const isApprovalProcessNotStarted = checkIsApprovalProcessNotStarted(expense)

    useEffect(() => {
        if (expenseId && expenseStatus !== undefined && requestedBy) {
            setIsLoading(true)
            const [table, adjacentExpenseLabelRes] = ExpenseService.getTableAndAdjacentExpenseLabel(expenseStatus, requestedBy, activeTab, isApprovalProcessNotStarted)
            ExpenseHttpService.getAdjacentExpenses(expenseId, table)
                .then(({ prevId, nextId }: PrevAndNextDocsResponse) =>
                    setAdjacentExpenses({
                        prevExpense: prevId ? generatePath(CompanyScopedRoutePathEnum.EXPENSE_DETAIL, { id: prevId }) : "",
                        nextExpense: nextId ? generatePath(CompanyScopedRoutePathEnum.EXPENSE_DETAIL, { id: nextId }) : "",
                    }),
                )
                .catch(() => setHasError(true))
                .finally(() => setIsLoading(false))
            setAdjacentExpenseLabel(adjacentExpenseLabelRes)
        }
    }, [expenseId, expenseStatus, requestedBy, activeTab])
    return { prevExpense, nextExpense, isExpenseSwitchingLoading: isLoading, hasError, adjacentExpenseLabel }
}

export const useRequestActionDisabled = () => {
    const employees = useEmployees({ excludeDeleted: true })
    const { data: vendors } = useVendorList({ ...defaultQueryParams, limit: 1 }) // Limit to 1 as we are interested only whether there are any vendors or not
    const costCenters = useCostCenters(true)
    const rolesMap = useRolesMap({ excludeDeleted: true })
    const activeCompanyProfile = useActiveCompanyProfile()

    const filteredEmployees = employees.filter((employeeToFilter) => {
        const role = rolesMap.get(employeeToFilter.activeCompanyProfile?.roleId)
        return (
            employeeToFilter.activeCompanyProfile &&
            !employeeToFilter.activeCompanyProfile.deleted &&
            ((isRightGranted(role?.rights, RightEnum.EXPENSE__ALL__APPROVE) && employeeToFilter.email !== PROCESS_ENV.FINWAY_ADMIN_EMAIL) ||
                (isRightGranted(role?.rights, RightEnum.EXPENSE__TEAM__APPROVE) && employeeToFilter.activeCompanyProfile.team === activeCompanyProfile.team))
        )
    })

    return vendors?.totalDocs === 0 || costCenters.length === 0 || filteredEmployees.length === 0
}

type UpdateClosure<T> = (split: ItemSplit) => T

export const useExpenseHook = (
    expense: Expense,
    expenseForm: FormInstance<Partial<Expense<string> | PerDiem | Hospitality | Mileage | TripFolder> | Filler>,
    updateExpense: (data: ExpenseData) => void,
    isLinked: boolean,
    setIsLinked: (linked: boolean) => void,
    isForcedSync?: boolean,
) => {
    const { onNameChange, onTaxRateSplitUpdate, onNetPriceUpdate, onTaxPriceUpdate, onGrossPriceUpdate } = useItemsHook(expense, expenseForm, updateExpense, isLinked, isForcedSync)
    const taxValues = useTaxes()
    // when isForcedSync is true, where changes in the items MUST affect the total amounts to keep them synced at all times.
    const [shouldRecalculateAmounts, setShouldRecalculateAmounts] = useStateIfMounted(false)
    const priceCalculator = useMemo(() => new PriceCalculator(expense.currency), [expense.currency])
    const onAddItem = (addItemAtTheBeginning = false) => {
        const costPropsDefaults = {
            costCenter: expense?.costCenter,
            costCenter2: expense?.costCenter2,
            expenseAccount: expense?.expenseAccount,
        }
        // backwards compatibility:
        // if old purchase request or there is existing item split, keep it as it.
        const isPurchaseRequest = checkIsPurchaseRequest(expense)
        if (expense.splitType === SplitTypeEnum.ITEM || expense.kind === ExpenseKindEnum.HOSPITALITY || expense.isReimbursement || isPurchaseRequest) {
            const newItemSplit = expenseInitializer.initializeNewItemSplit(taxValues, costPropsDefaults, expense.kind as ExpenseKindEnum)
            updateExpense({
                splits: addItemAtTheBeginning ? [newItemSplit, ...expense.splits] : [...expense.splits, newItemSplit],
                splitType: SplitTypeEnum.ITEM,
            })
        } else {
            // turn split into line item
            const newLineItemSplit = expenseInitializer.initializeNewLineItemSplit(taxValues, costPropsDefaults, expense.kind as ExpenseKindEnum)
            const existingSplits = expense.splits.map((split: InvoiceSplit | LineItem) => {
                if ("percentage" in split) {
                    const { percentage, ...rest } = split
                    return rest as LineItem
                }
                return split
            })

            updateExpense({
                splits: addItemAtTheBeginning ? [newLineItemSplit, ...existingSplits] : [...existingSplits, newLineItemSplit],
                splitType: SplitTypeEnum.LINE_ITEM,
            })
        }
    }

    const onDeleteAllItems = () => {
        if (expense.splitType === SplitTypeEnum.LINE_ITEM) {
            const splits = expense.splits as Array<LineItem>

            const newSplits = splits.map((split) => {
                if (split.purchaseOrderLink) {
                    return split
                }
                return undefined
            })

            updateExpense({ splits: newSplits.filter((split) => split !== undefined) })
            return
        }

        updateExpense({ splits: [] })
    }

    const onSelect = ({ key, domEvent }: MenuInfo): void => {
        domEvent.stopPropagation()
        switch (key) {
            case "add_item":
                onAddItem()
                break
            case "delete_all_items":
                onDeleteAllItems()
                break
            case "link":
                setIsLinked(!isLinked)
                break
            default:
                break
        }
    }

    const onTotalNetPriceUpdate = useDebouncedCallback((totalNetPrice: number) => {
        const { taxRate } = expense
        const dineroTotalNet = toDinero(totalNetPrice)
        const totalTaxPrice =
            taxRate && taxRate?._id !== NO_TAX_RATE
                ? toAmount(calculatePercentage({ price: toDinero(totalNetPrice), taxRate: getTax(taxValues, taxRate?._id) }))
                : expense.totalTaxPrice
        const totalGrossPrice = toAmount(dineroTotalNet.add(toDinero(totalTaxPrice)))
        updateExpense({
            totalNetPrice,
            ...(isLinked ? { totalGrossPrice, totalTaxPrice } : {}),
        })
    }, DEBOUNCE_DURATION_LONG)

    const onTaxRateUpdate = (taxRateId: string, priceSource: PriceSourceEnum) => {
        const taxRate = taxRateId === NO_TAX_RATE ? { _id: NO_TAX_RATE } : TaxService.getTaxById({ id: taxRateId, includeDeleted: false })

        const { totalNetPrice, totalTaxPrice, totalGrossPrice } =
            priceSource === PriceSourceEnum.NET_PRICE
                ? priceCalculator.totalsFromNetPrice({ totalNetPrice: expense.totalNetPrice, taxRate: taxRate._id === NO_TAX_RATE ? 0 : (taxRate as Tax).taxRate })
                : priceCalculator.totalsFromGrossPrice({ totalGrossPrice: expense.totalGrossPrice, taxRate: taxRate._id === NO_TAX_RATE ? 0 : (taxRate as Tax).taxRate })

        updateExpense({
            taxRate,
            ...(isLinked ? { totalTaxPrice, totalGrossPrice, totalNetPrice } : {}),
        })
    }

    const onTotalTaxPriceUpdate = (totalTaxPrice: number, priceSource: PriceSourceEnum.NET_PRICE) => {
        // we unset the taxRate when we manually update the total tax price
        if (!isLinked) {
            updateExpense({ totalTaxPrice })
            return
        }

        const updateObject = { totalTaxPrice, taxRate: { _id: NO_TAX_RATE } }
        switch (priceSource) {
            case PriceSourceEnum.NET_PRICE:
                const { totalGrossPrice } = priceCalculator.totalsFromNetPriceAndTaxPrice({ totalTaxPrice, totalNetPrice: expense.totalNetPrice })
                updateExpense({ ...updateObject, totalGrossPrice })
                break
            default:
                break
        }
    }

    const onTotalGrossPriceUpdate = useDebouncedCallback((totalGrossPrice: number) => {
        if (!isLinked) {
            updateExpense({ totalGrossPrice })
            return
        }

        if (expense.taxRate && expense.taxRate?._id !== NO_TAX_RATE) {
            const tax = getTax(taxValues, expense.taxRate?._id)
            const { totalTaxPrice, totalNetPrice } = priceCalculator.totalsFromGrossPrice({ totalGrossPrice, taxRate: tax })
            updateExpense({
                totalNetPrice,
                totalTaxPrice,
                totalGrossPrice,
            })
        } else {
            const netPriceDifference = totalGrossPrice - expense.totalTaxPrice
            const totalNetPrice = netPriceDifference < 0 ? 0 : roundNumberTo2Decimals(netPriceDifference)
            const totalTaxPrice = netPriceDifference < 0 ? 0 : expense.totalTaxPrice
            updateExpense({ totalNetPrice, totalGrossPrice, totalTaxPrice })
        }
    }, DEBOUNCE_DURATION_LONG)

    useEffect(() => {
        if (shouldRecalculateAmounts) {
            updateExpense(syncTotalExpenseAmountsToItems(expense.splits))
            setShouldRecalculateAmounts(false)
        }
    }, [shouldRecalculateAmounts])

    return {
        onAddItem,
        onSelect,
        onTaxRateUpdate,
        onTaxPriceUpdate,
        onNetPriceUpdate,
        onTaxRateSplitUpdate,
        onTotalNetPriceUpdate,
        onTotalTaxPriceUpdate,
        onTotalGrossPriceUpdate,
        onGrossPriceUpdate,
        onNameChange,
    }
}

export const useItemsHook = (
    expense: Expense,
    expenseForm: FormInstance<Partial<Expense<string> | PerDiem | Hospitality | Mileage | TripFolder> | Filler>,
    updateExpense: (data: ExpenseData) => void,
    isLinked: boolean,
    isForcedSync?: boolean,
) => {
    const taxValues = useTaxes()
    // when isForcedSync is true, where changes in the items MUST affect the total amounts to keep them synced at all times.
    const [shouldRecalculateAmounts, setShouldRecalculateAmounts] = useStateIfMounted(false)
    const priceCalculator = useMemo(() => new PriceCalculator(expense.currency), [expense.currency])
    const recalculateAmountsCallback = useDebouncedCallback(() => setShouldRecalculateAmounts(true), DEBOUNCE_DURATION_SHORT)

    const applyUpdateClosureAtIndex = <T>(split: ItemSplit, index: number, targetIndex: number, updateClosure: UpdateClosure<T>): ItemSplit =>
        index === targetIndex ? { ...split, ...updateClosure(split) } : split

    const updateItemSplit = <T>(index: number, updateClosure: UpdateClosure<T>) => {
        const splits = expenseForm.getFieldValue("splits")
        const updatedSplits = splits.map((split: ItemSplit | LineItem, i: number) => applyUpdateClosureAtIndex(split, i, index, updateClosure))
        updateExpense({ splits: updatedSplits })
        if (isForcedSync && expense.splits?.length > 0 && expense.splitType === SplitTypeEnum.ITEM) {
            recalculateAmountsCallback.callback()
        }
        return updatedSplits[index]
    }

    const onFieldUpdate = useDebouncedCallback((index: number, field: string, value: any) => {
        updateItemSplit(index, () => ({ [field]: value }))
    }, DEBOUNCE_DURATION_LONG)

    const onNameChange = (name: string, index: number) => {
        onFieldUpdate.callback(index, "name", name)
    }

    const onTaxRateSplitUpdate = (index: number, taxRateId: string, priceSource: PriceSourceEnum) => {
        // Note: for general tax rate update after split tax rate update, see getCommonTaxRateAcrossItems
        updateItemSplit(index, (itemSplit: ItemSplit) => {
            const taxRate = taxRateId === NO_TAX_RATE ? { _id: NO_TAX_RATE } : TaxService.getTaxById({ id: taxRateId, includeDeleted: false })

            const {
                totalNetPrice: netPrice,
                totalTaxPrice: taxPrice,
                totalGrossPrice: grossPrice,
            } = priceSource === PriceSourceEnum.NET_PRICE
                ? priceCalculator.totalsFromNetPrice({ totalNetPrice: itemSplit.netPrice, taxRate: taxRate._id === NO_TAX_RATE ? 0 : (taxRate as Tax).taxRate })
                : priceCalculator.totalsFromGrossPrice({ totalGrossPrice: itemSplit.grossPrice, taxRate: taxRate._id === NO_TAX_RATE ? 0 : (taxRate as Tax).taxRate })

            return {
                taxRate,
                ...(isLinked ? { taxPrice, grossPrice, netPrice } : {}),
            }
        })
    }

    const onNetPriceUpdate = (index: number, netPrice: number) => {
        updateItemSplit(index, (itemSplit) => {
            const taxRate = itemSplit.taxRate?._id === NO_TAX_RATE ? undefined : getTax(taxValues, itemSplit.taxRate?._id!)
            const splitHasTaxPriceButNoTaxRate = itemSplit.taxPrice && itemSplit.taxRate?._id === NO_TAX_RATE

            const { totalGrossPrice: grossPrice, totalTaxPrice: taxPrice } = splitHasTaxPriceButNoTaxRate
                ? priceCalculator.totalsFromNetPriceAndTaxPrice({ totalNetPrice: netPrice, totalTaxPrice: itemSplit.taxPrice })
                : priceCalculator.totalsFromNetPrice({
                      totalNetPrice: netPrice,
                      taxRate,
                  })
            const updatedItemSplit = {
                netPrice,
                ...(isLinked
                    ? {
                          grossPrice,
                          taxPrice,
                      }
                    : {}),
            }

            return updatedItemSplit
        })
    }

    const onTaxPriceUpdate = (index: number, taxPrice: number, priceSource: PriceSourceEnum) => {
        // we unset the taxRate when we manually update the total tax price
        if (!isLinked) {
            updateItemSplit(index, (_itemSplit: ItemSplit) => ({ taxPrice }))
            return
        }

        const updateObject = {
            taxPrice,
            taxRate: { _id: NO_TAX_RATE },
        }

        switch (priceSource) {
            case PriceSourceEnum.NET_PRICE:
                updateItemSplit(index, (itemSplit: ItemSplit) => ({
                    ...updateObject,
                    grossPrice: addPrices([itemSplit.netPrice, taxPrice]),
                }))
                break
            case PriceSourceEnum.GROSS_PRICE:
                updateItemSplit(index, (itemSplit: ItemSplit) => ({
                    ...updateObject,
                    netPrice: subtractPrices(itemSplit.grossPrice, [taxPrice]),
                }))
                break
            default:
                break
        }
    }

    const onGrossPriceUpdate = (index: number, grossPrice: number) => {
        updateItemSplit(index, ({ taxRate, taxPrice }: ItemSplit) => {
            if (!isLinked) {
                return { grossPrice }
            }

            if (taxRate?._id !== NO_TAX_RATE) {
                const tax = getTax(taxValues, taxRate?._id!)
                const { totalNetPrice, totalTaxPrice } = priceCalculator.totalsFromGrossPrice({ totalGrossPrice: grossPrice, taxRate: tax })
                return {
                    taxPrice: totalTaxPrice,
                    netPrice: totalNetPrice,
                    grossPrice,
                }
            }

            const netPriceDifference = toAmount(toDinero(grossPrice).subtract(toDinero(taxPrice)))

            if (netPriceDifference <= 0) {
                return { netPrice: 0, taxPrice: 0, grossPrice }
            }

            return { netPriceDifference, taxPrice, grossPrice }
        })
    }

    const onQuantityUpdate = (index: number, quantity: number) => {
        const updatedSplit = updateItemSplit(index, () => ({ quantity }))
        if (updatedSplit.unitPrice) {
            onNetPriceUpdate(index, updatedSplit.unitPrice * quantity)
        }
    }

    const onUnitPriceUpdate = (index: number, unitPrice: number) => {
        const updatedSplit = updateItemSplit(index, () => ({ unitPrice }))
        if (updatedSplit.quantity) {
            onNetPriceUpdate(index, updatedSplit.unitPrice * updatedSplit.quantity)
        }
    }

    useEffect(() => {
        if (shouldRecalculateAmounts) {
            updateExpense(syncTotalExpenseAmountsToItems(expense.splits))
            setShouldRecalculateAmounts(false)
        }
    }, [shouldRecalculateAmounts])

    const onAddLineItem = (formAdd: FormListOperation["add"]) => {
        const formSplits = expenseForm.getFieldValue("splits")

        const isPurchaseRequest = checkIsPurchaseRequest(expense)

        if (formSplits.length === 0) {
            const costPropsDefaults = {
                costCenter: expense.costCenter,
                costCenter2: expense.costCenter2,
                expenseAccount: expense.expenseAccount,
            }
            const newSplit = isPurchaseRequest
                ? expenseInitializer.initializeNewItemSplit(taxValues, costPropsDefaults, expense.kind as ExpenseKindEnum)
                : expenseInitializer.initializeNewLineItemSplit(taxValues, costPropsDefaults, expense.kind as ExpenseKindEnum)
            formAdd(newSplit)
            updateExpense({ splits: [newSplit], splitType: isPurchaseRequest ? SplitTypeEnum.ITEM : SplitTypeEnum.LINE_ITEM })
            return
        }
        const previousSplit = formSplits[formSplits.length - 1]
        const costPropsDefaultsFromPreviousSplit = {
            costCenter: previousSplit.costCenter,
            costCenter2: previousSplit.costCenter2,
            expenseAccount: previousSplit.expenseAccount,
        }
        const newSplit = isPurchaseRequest
            ? expenseInitializer.initializeNewItemSplit(taxValues, costPropsDefaultsFromPreviousSplit, expense.kind as ExpenseKindEnum)
            : expenseInitializer.initializeNewLineItemSplit(taxValues, costPropsDefaultsFromPreviousSplit, expense.kind as ExpenseKindEnum)
        formAdd(newSplit)
        updateExpense({ splits: [...formSplits, newSplit], splitType: isPurchaseRequest ? SplitTypeEnum.ITEM : SplitTypeEnum.LINE_ITEM })
    }

    const onRemoveLineItem = useCallback(
        (index: number) => {
            updateExpense({ splits: expense.splits.filter((_, i) => i !== index) })
        },
        [expense.splits],
    )

    useEffect(() => {
        if (expense.splitType === SplitTypeEnum.LINE_ITEM && expense.splits.length > 0) {
            const isCostCenterUniform = doAllElementsHaveTheSameFieldValue(expense?.splits, "costCenter")
            const isCostCenter2Uniform = doAllElementsHaveTheSameFieldValue(expense?.splits, "costCenter2")
            const isExpenseAccountUniform = doAllElementsHaveTheSameFieldValue(
                expense.splits.map((s) => s.expenseAccount),
                "_id",
            )

            if (isCostCenterUniform && expense.costCenter !== expense.splits?.[0]?.costCenter) {
                updateExpense({ costCenter: expense.splits?.[0]?.costCenter })
            }
            if (isCostCenter2Uniform && expense.costCenter2 !== expense.splits?.[0]?.costCenter2) {
                updateExpense({ costCenter2: expense.splits?.[0]?.costCenter2 })
            }
            if (isExpenseAccountUniform && expense.expenseAccount?._id !== expense.splits?.[0]?.expenseAccount?._id) {
                updateExpense({ expenseAccount: expense.splits?.[0]?.expenseAccount })
            }
        }
    }, [expense])

    return {
        onFieldUpdate,
        onNameChange,
        onTaxRateSplitUpdate,
        onNetPriceUpdate,
        onTaxPriceUpdate,
        onGrossPriceUpdate,
        onQuantityUpdate,
        onUnitPriceUpdate,
        onAddLineItem,
        onRemoveLineItem,
    }
}

export const setMultipleExpensesAsDoneAndReturnFailures = async (expensesArrayOrExpenseQuery: Array<Expense> | string) => {
    if (Array.isArray(expensesArrayOrExpenseQuery)) {
        const expenseIds = expensesArrayOrExpenseQuery.map(({ id }) => id)
        return setMultipleExpensesAsDoneByExpenseIds(expenseIds)(store.dispatch)
    }

    return setAllExpensesAsDone(expensesArrayOrExpenseQuery)(store.dispatch)
}

export const markMultipleExpensesAsDone = async (expensesArrayOrExpenseQuery: Array<Expense> | string) => {
    try {
        const filteredOutExpenseNumbers = await setMultipleExpensesAsDoneAndReturnFailures(expensesArrayOrExpenseQuery)

        if (filteredOutExpenseNumbers.length) {
            NotificationService.send(
                NotificationTypeEnum.WARNING,
                i18n.t(`notification:request.exported_bulk_partial.title`),
                i18n.t(`notification:request.exported_bulk_partial.message`, { expenses: filteredOutExpenseNumbers.join(", ") }),
                0,
            )
        } else {
            // TODO: check if this notification is ok w/ product
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.exported_bulk.title`), i18n.t(`notification:request.exported_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") {
            return
        }
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.exported.title`))
        throw err
    }
}

export const applyExpenseUpdate = (expense: Expense, notificationLabel: string, skipHandling = false, refetchExpenseTables = true) =>
    updateExpense(
        expense.id,
        expense,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => {
            if (!skipHandling) {
                NotificationService.send(
                    NotificationTypeEnum.SUCCESS,
                    i18n.t(`notification:request.${notificationLabel}.title`),
                    i18n.t(`notification:request.${notificationLabel}.message`),
                )
            }
        })
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            if (!skipHandling) {
                NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.${notificationLabel}.title`))
            }
            throw err
        })

export const detachExpense = (expensePara: Expense) => {
    const expense = copyDeeply(expensePara, Expense)
    return detachOneExpense(expense.id)(store.dispatch)
}

export const restoreExpense = (expensePara: Expense, currentUserId: string) => {
    const expense = copyDeeply(expensePara, Expense)
    expense.deleted = false
    expense.dateDeleted = undefined

    return applyExpenseUpdate(expense, "restore")
}

export const sendEmailReminder = async (employees: Array<Employee>, expense: Expense) => {
    try {
        await sendReminder(expense.id)(store.dispatch)
        if (expense.status === ExpenseStatusEnum.DOCS_NEEDED) {
            const requester = GetEmployeeById(employees, expense.requestedBy._id)
            if (!requester) {
                NotificationService.send(NotificationTypeEnum.WARNING, "", i18n.t("notification:request.reminder_not_sent.message"))
                return
            }
        } else {
            const approver = await ApprovalProcessService.getNextApprover(expense.approvalProcesses)
            if (!approver) {
                NotificationService.send(NotificationTypeEnum.WARNING, "", i18n.t("notification:request.reminder_not_sent.message"))
                return
            }
        }
        NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t("notification:request.reminder_sent.title"), i18n.t("notification:request.reminder_sent.message"))
    } catch (error: any) {
        NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:request.send_reminder.title"))
    }
}

export const reviewFolder = async (folder: TripFolder, subExpenses: Array<Expense> = []) => {
    const { processableSubExpenses } = getFolderProgressionInfo(folder, subExpenses)

    if (processableSubExpenses.some((processableSubExpense) => processableSubExpense.invoiceNumber && !isExtendedAlphanumeric(processableSubExpense.invoiceNumber))) {
        try {
            await DialogService.confirmDatevWarningPromise(true)
        } catch {
            return
        }
    }

    return reviewMultipleExpenses([folder.id, ...processableSubExpenses.map((e) => e.id)])(store.dispatch).catch((err) => {
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t("error:error"))
    })
}

export const approveExpenseRequest = async (expense: Expense, scope: ApprovalScopeEnum, user: Employee, approverName?: string) => {
    if (AuthzService.isLoggedInUserAllowedToApproveExpense(expense) && approverName) {
        if (!(await ExpenseService.confirmAdminNotOwnApproval(expense, scope, user, approverName))) {
            return
        }
    }
    return approveExpense(
        expense.id,
        scope,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.approved.title`), i18n.t(`notification:request.approved.message`)))
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.approved.title`))
            throw err
        })
}

export const rejectExpenseRequest = ({
    expense,
    rejectionNote,
    history,
    scope,
}: {
    expense: Expense
    rejectionNote: string
    history: RouterChildContext["router"]["history"]
    scope?: ApprovalScopeEnum
}) =>
    rejectExpense(
        expense.id,
        rejectionNote,
        scope,
    )(store.dispatch)
        .then(() => {
            if (isFolderExpense(expense)) {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    i18n.t(`notification:trip_folder.rejected.title`),
                    i18n.t(`notification:trip_folder.rejected.message`),
                    NotificationDurationSec.REQUEST_CREATION,
                    undefined,
                    {
                        onClick: createLinkToRequestFunction(expense, history),
                        text: i18n.t("notification:trip_folder.rejected.link"),
                    },
                )
            } else {
                NotificationService.send(
                    NotificationTypeEnum.WARNING,
                    i18n.t(`notification:request.rejected.title`),
                    i18n.t(`notification:request.rejected.message`),
                    NotificationDurationSec.REQUEST_CREATION,
                    undefined,
                    {
                        onClick: createLinkToRequestFunction(expense, history),
                        text: i18n.t("notification:request.rejected.link"),
                    },
                )
            }
        })
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.rejected.title`))
            throw err
        })

export const cancelExpenseRequest = (expense: Expense, shouldUseDeleteLabel = false, onCancel?: () => void) => {
    const { confirm } = Modal
    const isTripFolder = expense.kind === ExpenseKindEnum.TRIP_FOLDER

    const confirmTranslations = createCancelExpenseRequestTranslationStrings(isTripFolder, shouldUseDeleteLabel, i18n.t)

    const translationType = isTripFolder ? "trip_folder" : "request"

    confirm({
        ...confirmTranslations,
        type: "warning",
        onOk() {
            return new Promise<void>((resolve, reject) => {
                deleteExpense(expense.id)(store.dispatch)
                    .then(() => {
                        NotificationService.send(
                            NotificationTypeEnum.WARNING,
                            i18n.t(`notification:${translationType}.cancelled.title`),
                            i18n.t(`notification:${translationType}.cancelled.message`),
                        )
                        resolve()
                    })
                    .catch((err) => {
                        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:${translationType}.cancel.title`))
                        reject()
                    })
            })
                .then(() => onCancel && onCancel())
                .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:${translationType}.cancel.title`)))
        },
        onCancel() {},
    })
}

export const approveFolderRequest = async (folder: TripFolder, scope: ApprovalScopeEnum, user: Employee, approverName?: string) => {
    if (AuthzService.isLoggedInUserGlobalApprover() && approverName) {
        if (!(await ExpenseService.confirmAdminNotOwnApproval(folder, scope, user, approverName))) {
            return
        }
    }

    return approveFolder(
        folder.id,
        scope,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.approved.title`), i18n.t(`notification:request.approved.message`)))
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.approved.title`))
            throw err
        })
}

export const handleTripFolderSubmit = (folder: TripFolder, subExpenses: Array<Expense>) =>
    submitFolder(folder.id)(store.dispatch)
        .then(() => {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:trip_folder.submitted.title`), i18n.t(`notification:trip_folder.submitted.message`))
            const showMultiCurrenciesModal = hasMultipleCurrencies(folder, subExpenses)
            if (showMultiCurrenciesModal) {
                Modal.warning({
                    title: i18n.t("confirm:request.submit_folder_with_different_currencies.title"),
                    content: i18n.t("confirm:request.submit_folder_with_different_currencies.message"),
                    okText: i18n.t("confirm:request.submit_folder_with_different_currencies.confirm"),
                })
            }
        })
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.rejected.title`))
            throw err
        })

export const markExpenseAsReviewed = async (expense: Expense, refetchExpenseTables = true) => {
    // confirm reporting only status
    const PAYMENT_FLOW_TO_CONFIRM = [ExpensePaymentFlowInformationEnum.ALREADY_PAID, ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID]
    if (
        expense.paymentFlowInformation &&
        PAYMENT_FLOW_TO_CONFIRM.includes(expense.paymentFlowInformation) &&
        !(await DialogService.confirmPaymentFlowInformationAlreadyPaidWarningOnReview(expense.paymentFlowInformation))
    ) {
        return
    }

    return reviewExpense(
        expense.id,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.reviewed.title`), i18n.t(`notification:request.reviewed.message`)))
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.reviewed.title`))
            throw err
        })
}

export const markExpenseAsPaid = (expense: Expense, refetchExpenseTables = true) =>
    setExpensePaid(
        expense.id,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.paid.title`), i18n.t(`notification:request.paid.message`)))
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.paid.title`))
            throw err
        })

export const markExpenseAsDone = (expense: Expense, refetchExpenseTables = true) =>
    setExpenseDone(
        expense.id,
        refetchExpenseTables,
    )(store.dispatch)
        .then(() => {
            if (isFolderExpense(expense)) {
                NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:trip_folder.exported.title`), i18n.t(`notification:trip_folder.exported.message`))
            } else {
                NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.exported.title`), i18n.t(`notification:request.exported.message`))
            }
        })
        .catch((err) => {
            if (err === "cancelled") {
                return
            }
            NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.exported.title`))
            throw err
        })

export const markMultipleExpensesAsReviewed = async (expenses: Array<Expense>) => {
    // confirm reporting only status
    const PAYMENT_FLOW_TO_CONFIRM = [ExpensePaymentFlowInformationEnum.ALREADY_PAID, ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID]
    const paymentFlowSet = expenses.find((expense) => expense.paymentFlowInformation && PAYMENT_FLOW_TO_CONFIRM.includes(expense.paymentFlowInformation))
    if (paymentFlowSet && !(await DialogService.confirmPaymentFlowsOnBulkReview())) {
        return
    }

    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await reviewMultipleExpenses(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            NotificationService.send(
                NotificationTypeEnum.WARNING,
                i18n.t(`notification:request.reviewed_bulk_partial.title`),
                i18n.t(`notification:request.reviewed_bulk_partial.message`, { expenses: filteredOutExpenseNumbers }),
                0,
            )
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.reviewed_bulk.title`), i18n.t(`notification:request.reviewed_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") {
            return
        }
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.reviewed.title`))
        throw err
    }
}

export const markMultipleExpensesAsPaid = async (expenses: Array<Expense>) => {
    try {
        const expenseIds = expenses.map(({ id }) => id)
        const filteredOutExpenseNumbers = await setMultipleExpensesAsPaid(expenseIds)(store.dispatch)

        if (filteredOutExpenseNumbers.length) {
            NotificationService.send(
                NotificationTypeEnum.WARNING,
                i18n.t(`notification:request.paid_bulk_partial.title`),
                i18n.t(`notification:request.paid_bulk_partial.message`, { expenses: filteredOutExpenseNumbers }),
                0,
            )
        } else {
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t(`notification:request.paid_bulk.title`), i18n.t(`notification:request.paid_bulk.message`))
        }
    } catch (err) {
        if (err === "cancelled") {
            return
        }
        NotificationService.showErrorNotificationBasedOnResponseError(err, i18n.t(`error:request.paid.title`))
        throw err
    }
}

export const useIsExpenseAccountMandatory = () => {
    const company = useCompany()
    const expenseAccounts = useExpenseAccounts()
    const isExpenseAccountsExists = expenseAccounts && expenseAccounts.length > 0
    const isGobdCompliant = !!company.gobdCompliance?.enforceGobdCompliantInvoice

    return isGobdCompliant && isExpenseAccountsExists
}
